import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Loader2, ChevronLeft, ChevronRight, Phone, Video, Briefcase } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { formatTimestamp } from "../../utils/helpers";

interface InterviewCandidate {
  id: number;
  firstName: string;
  lastName: string;
}

interface Interview {
  id: number;
  interviewType: "PHONE" | "VIDEO" | "IN_PERSON";
  scheduledAt: string;
  durationMinutes: number;
  location: string | null;
  status: "SCHEDULED" | "COMPLETED" | "CANCELLED" | "NO_SHOW";
  outcome: "PASS" | "FAIL" | "PENDING" | null;
  candidate: InterviewCandidate;
}

interface Pagination {
  total: number;
  page: number;
  pages: number;
}

const getStatusStyle = (status: string) => {
  switch (status) {
    case "SCHEDULED":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "COMPLETED":
      return "bg-green-50 text-green-700 border-green-100";
    case "CANCELLED":
      return "bg-red-50 text-red-700 border-red-100";
    case "NO_SHOW":
      return "bg-orange-50 text-orange-700 border-orange-100";
    default:
      return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const formatStatusLabel = (status: string) => {
  switch (status) {
    case "SCHEDULED": return "Scheduled";
    case "COMPLETED": return "Completed";
    case "CANCELLED": return "Cancelled";
    case "NO_SHOW": return "No Show";
    default: return status;
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case "PHONE": return <Phone size={14} className="text-indigo-500" />;
    case "VIDEO": return <Video size={14} className="text-purple-500" />;
    case "IN_PERSON": return <Briefcase size={14} className="text-blue-500" />;
    default: return null;
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case "PHONE": return "Phone";
    case "VIDEO": return "Video";
    case "IN_PERSON": return "In-Person";
    default: return type;
  }
};

const PAGE_SIZE = 10;

const InterviewsList: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_INTERVIEW_SCHEDULE) && !ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE)) {
    return <AccessRestricted />;
  }
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);

  const fetchInterviews = useCallback(async (currentPage: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(currentPage));
      params.set("limit", String(PAGE_SIZE));
      if (statusFilter) params.set("status", statusFilter);

      const res = await api.get<{
        success: boolean;
        interviews: Interview[];
        pagination: Pagination;
      }>(`/api/interviews?${params.toString()}`);

      if (res.success) {
        setInterviews(res.interviews);
        setPagination(res.pagination);
      }
    } catch (error) {
      console.error("Failed to fetch interviews:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchInterviews(page);
  }, [page, statusFilter, fetchInterviews]);

  const filteredInterviews = useMemo(() => {
    if (searchQuery === "") return interviews;
    const q = searchQuery.toLowerCase();
    return interviews.filter((interview) => {
      const candidateName = `${interview.candidate?.firstName || ""} ${interview.candidate?.lastName || ""}`.toLowerCase();
      const type = interview.interviewType.toLowerCase();
      const status = interview.status.toLowerCase();
      const location = (interview.location || "").toLowerCase();
      return (
        candidateName.includes(q) ||
        type.includes(q) ||
        status.includes(q) ||
        location.includes(q)
      );
    });
  }, [searchQuery, interviews]);

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "SCHEDULED", label: "Scheduled" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "NO_SHOW", label: "No Show" },
  ];

  return (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-bold text-gray-900 text-lg">Interview Schedule</h4>
          <p className="text-gray-500 text-sm font-medium">
            Upcoming and recent interviews
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <Input
            placeholder="Search interviews..."
            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                statusFilter === opt.value
                  ? "bg-slate-950 text-white"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Interview Cards */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filteredInterviews.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <p className="text-sm font-medium">
              {searchQuery || statusFilter ? "No interviews match your filters" : "No interviews scheduled yet"}
            </p>
          </div>
        ) : (
          filteredInterviews.map((interview) => (
            <div
              key={interview.id}
              className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-900">
                    {getTypeLabel(interview.interviewType)} Interview
                  </p>
                  {getTypeIcon(interview.interviewType)}
                </div>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  {interview.candidate?.firstName} {interview.candidate?.lastName}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  {formatTimestamp(interview.scheduledAt)} · {interview.durationMinutes} min
                  {interview.location ? ` · ${interview.location}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <Badge
                  variant="outline"
                  className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusStyle(
                    interview.status
                  )}`}
                >
                  {formatStatusLabel(interview.status)}
                </Badge>
                <button
                  onClick={() =>
                    navigate(`/recruitment/candidates/${interview.candidate?.id}`)
                  }
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="View Candidate"
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 font-medium">
            Showing {((pagination.page - 1) * PAGE_SIZE) + 1}–{Math.min(pagination.page * PAGE_SIZE, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg border-gray-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-bold text-gray-900 min-w-[3rem] text-center">
              {pagination.page} / {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg border-gray-200"
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages || loading}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewsList;
